import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useAppStore } from '@/lib/app-store'
import { OPEN_PREFERENCES_EVENT, readConsent, writeConsent, type ConsentCategories } from '@/lib/cookie-consent'
import { Button } from '@/components/ui/button'

// Lazy overlay (the repo's standard pattern): the preferences dialog — and with it the whole dialog stack
// (@base-ui dialog, scroll lock, Switch) — is `lazy()`-imported so it stays out of the entry chunk; the
// banner itself is shell chrome on every page (__root imports it statically). Mount is gated on
// `dialogMounted` (flips true on FIRST open and never back) while the real `open` state drives the Dialog,
// so exit animations still play.
const CookiePreferencesDialog = lazy(() =>
  import('./cookie-preferences-dialog').then((m) => ({ default: m.CookiePreferencesDialog })),
)

export function CookieBanner() {
  const { t } = useLingui()
  const sdk = useAppStore((state) => state.store.sdk)

  // `decided` mirrors whether the visitor has made a choice (banner hidden once true). Seeded from
  // the cookie on mount — safe because the banner only renders client-side (wrapped in ClientOnly).
  const [decided, setDecided] = useState(() => readConsent().decided)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMounted, setDialogMounted] = useState(false)
  // Draft toggle state for the preferences dialog. Committed only on an explicit save/accept/reject,
  // so flipping a switch no longer writes a cookie immediately (clearer, reversible UX).
  const [draft, setDraft] = useState<ConsentCategories>({ statistics: false, marketing: false })

  // Allow the dialog to be reopened from anywhere (e.g. the footer "Cookie settings" link) so users
  // can change or withdraw consent as easily as they gave it.
  useEffect(() => {
    const open = () => {
      const current = readConsent()
      setDraft({ statistics: current.statistics, marketing: current.marketing })
      setDialogMounted(true)
      setDialogOpen(true)
    }
    window.addEventListener(OPEN_PREFERENCES_EVENT, open)
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open)
  }, [])

  const commit = useCallback((categories: ConsentCategories) => {
    writeConsent(categories)
    setDecided(true)
    setDialogOpen(false)
  }, [])

  const acceptAll = useCallback(() => commit({ statistics: true, marketing: true }), [commit])
  const rejectAll = useCallback(() => commit({ statistics: false, marketing: false }), [commit])
  const saveDraft = useCallback(() => commit(draft), [commit, draft])

  const openPreferences = useCallback(() => {
    const current = readConsent()
    setDraft({ statistics: current.statistics, marketing: current.marketing })
    setDialogMounted(true)
    setDialogOpen(true)
  }, [])

  // The SDK embeds the app inside a customer's iframe — no banner there; consent is the host's job.
  if (sdk) return null

  const description = t`At D'Anchiano we use our own- and third-party cookies to remember who you are and your preferences, analyze your behaviour to improve our products and deliver interest-based advertising. You can accept all cookies, reject the non-essential ones, or choose which to allow.`

  return (
    <>
      {!decided && (
        <div
          role="region"
          aria-label={t`Cookie consent`}
          className="cookie-banner bg-background fixed right-0 bottom-0 left-0 z-[1000] flex flex-col gap-4 p-4 shadow-[var(--footer-shadow)] sm:flex-row sm:items-center sm:justify-between print:hidden"
        >
          <div className="flex items-start gap-2.5">
            <span role="img" aria-label="cookie" className="text-2xl leading-none">
              🍪
            </span>
            <p className="text-sm">
              {description}{' '}
              <a href="/cookies-policy" target="_blank" rel="noopener noreferrer" className="underline">
                {t`Cookies policy`}
              </a>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:items-center">
            <Button type="button" variant="ghost" onClick={openPreferences}>
              {t`Customize`}
            </Button>
            <Button type="button" variant="outline" onClick={rejectAll}>
              {t`Reject all`}
            </Button>
            <Button type="button" onClick={acceptAll}>
              {t`Accept all`}
            </Button>
          </div>
        </div>
      )}

      {dialogMounted ? (
        <Suspense fallback={null}>
          <CookiePreferencesDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            description={description}
            draft={draft}
            setDraft={setDraft}
            rejectAll={rejectAll}
            saveDraft={saveDraft}
            acceptAll={acceptAll}
          />
        </Suspense>
      ) : null}
    </>
  )
}
